<template>
    <div v-if="activeSite && isUserStudenTeacherOrBetter">
        <h3 class="mb-3">Enviar Correo Electrónico</h3>

        <h4>Destinatarios</h4>
        <b-row>
            <b-col
                cols="12"
                md="4"
            >
                <b-form-group>
                    <label><strong>Docentes:</strong></label>
                    <b-form-checkbox-group
                        v-if="availableProfessorOptions"
                        v-model="form.professors"
                        stacked
                        switches
                        button-variant="dark"
                        name="recipient-professors"
                        :options="availableProfessorOptions"
                        :disabled="formDisabled"
                    />
                    <p v-else>No hay docentes en este sitio web.</p>
                </b-form-group>
            </b-col>
            <b-col
                cols="12"
                md="4"
            >
                <b-form-group>
                    <label><strong>Preparadores:</strong></label>
                    <b-form-checkbox-group
                        v-if="availableStudentTeacherOptions"
                        v-model="form.studentTeachers"
                        stacked
                        switches
                        button-variant="light"
                        name="recipient-studentTeachers"
                        :options="availableStudentTeacherOptions"
                        :disabled="formDisabled"
                    />
                    <p v-else>No hay preparadores en este sitio web.</p>
                </b-form-group>
            </b-col>
            <b-col
                cols="12"
                md="4"
            >
                <b-form-group>
                    <label><strong>Secciones:</strong></label>
                    <b-form-checkbox-group
                        v-if="getActiveSiteSectionOptions"
                        v-model="form.sectionIds"
                        stacked
                        switches
                        button-variant="dark"
                        name="recipient-sections"
                        :options="getActiveSiteSectionOptions"
                        :disabled="formDisabled"
                    />
                    <p v-else>No hay secciones en este sitio web.</p>
                </b-form-group>
            </b-col>
        </b-row>
        <hr />
        <h4>Mensaje</h4>
        <b-row align-v="center">
            <b-col
                cols="12"
                md="10"
                offset-md="1"
            >
                <b-form @submit.stop.prevent="onSubmit">
                    <b-form-group
                        id="input-group-email-subject"
                        label="*Asunto:"
                        label-for="input-email-subject"
                    >
                        <b-form-input
                            id="input-email-subject"
                            v-model.trim="$v.form.subject.$model"
                            name="emailSubject"
                            type="text"
                            required
                            :state="validateState('subject')"
                            :disabled="formDisabled"
                        />
                        <b-form-invalid-feedback id="input-email-subject-feedback">
                            <ul>
                                <li v-if="!$v.form.subject.required">Este campo es requerido.</li>
                            </ul>
                        </b-form-invalid-feedback>
                    </b-form-group>

                    <b-form-group
                        id="input-group-email-body"
                        label="*Mensaje:"
                        label-for="input-email-body"
                    >
                        <quillEditor v-model="form.body" :options="editorOptions" :disabled="formDisabled"/>
                    </b-form-group>

                    <div class="text-center pt-4">
                        <b-button
                            type="submit"
                            variant="primary"
                            size="lg"
                            :disabled="disableSubmitButton"
                        >
                            <b-icon-envelope /> Enviar
                        </b-button>
                    </div>
                </b-form>
            </b-col>
        </b-row>

    </div>
</template>

<script>
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'

import { quillEditor } from 'vue-quill-editor' // WYSIWYG Rich text editor
import { required } from 'vuelidate/lib/validators'
import { mapState, mapActions, mapGetters } from 'vuex'

export default {
    name: 'SiteSendEmail',
    components: {
        quillEditor
    },
    data () {
        return {
            formDisabled: false,
            editorOptions: {
                placeholder: 'Inserte Mensaje',
                modules: {
                    toolbar: [['bold', 'italic', 'underline', 'strike'], ['link']]
                }
            },
            form: {
                professors: [], // identityCardNumbers
                studentTeachers: [], // identityCardNumbers
                sectionIds: [],
                subject: '',
                body: ''
            }
        }
    },
    validations: {
        form: {
            subject: {
                required
            },
            body: {
                required
            }
        }
    },
    computed: {
        ...mapState([
            'activeSite'
        ]),
        ...mapGetters([
            'getActiveSiteProfessors',
            'getActiveSiteStudentTeachers',
            'getActiveSiteSectionOptions',
            'isUserStudenTeacherOrBetter'
        ]),
        availableProfessorOptions () {
            if (this.getActiveSiteProfessors) {
                return this.getActiveSiteProfessors.map(prof => {
                    return {
                        text: prof.name,
                        value: prof.identityCardNumber
                    }
                })
            }
            return null
        },
        availableStudentTeacherOptions () {
            if (this.getActiveSiteStudentTeachers) {
                return this.getActiveSiteStudentTeachers.map(sT => {
                    return {
                        text: sT.name,
                        value: sT.identityCardNumber
                    }
                })
            }
            return null
        },
        atLeastOneRecipient () {
            return this.form.professors.length || this.form.studentTeachers.length || this.form.sectionIds.length
        },
        disableSubmitButton () {
            return this.formDisabled || this.$v.form.$invalid || !this.atLeastOneRecipient
        }
    },
    methods: {
        ...mapActions([
            'sendEmailToSiteUsers'
        ]),
        validateState (name) {
            const { $dirty, $error } = this.$v.form[name]
            return $dirty ? !$error : null
        },
        resetForm () {
            this.form.professors = []
            this.form.studentTeachers = []
            this.form.sectionIds = []
            this.form.subject = ''
            this.form.body = ''
            this.$v.$reset()
        },
        async onSubmit () {
            let loader = null
            try {
                this.formDisabled = true
                loader = this.$root.$loading.show()
                const payloadData = { ...this.form, courseSiteId: this.activeSite.id }
                await this.sendEmailToSiteUsers(payloadData)
                this.$root.successToast(`Mensaje(s) Enviado(s) exitosamente.`)
                this.resetForm()
            } catch (err) {
                console.log({ err: err })
                this.$root.dangerToast('Ha ocurrido un error y no se ha podido enviar el/los mensaje(s), vuelva a intentar más tarde.')
            } finally {
                this.formDisabled = false
                loader.hide()
            }
        }
    }
}
</script>
